import axios from 'axios';
import { API_BASE_URL } from '@/lib/config';
import { getApiRequestHeaders } from '@/lib/api';

const geoApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Attach auth and App Check headers so signed-in users are counted under their
// authenticated rate limits instead of anonymous buckets.
geoApi.interceptors.request.use(
  async (config) => {
    const headers = await getApiRequestHeaders();
    config.headers = config.headers || {}
    Object.assign(config.headers, headers);
    return config;
  },
  (error) => Promise.reject(error)
);

export type GeoPropertySummary = {
  id: string;
  title?: string | null;
  property_type?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  price?: number | null;
  price_type?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lng?: number | null;
  formatted_address?: string | null;
  distance_km?: number | null;
  distance_m?: number | null;
  images?: { url: string; is_primary?: boolean }[];
  attributes?: Record<string, unknown> | null;
  source_url?: string | null;
  owner_id?: string | null;
  posted_by_id?: string | null;
  owner_name?: string | null;
  owner_username?: string | null;
  created_at?: string | null;
  show_map_pin?: boolean | null;
  is_location_exact_visible?: boolean | null;
  hide_exact_location?: boolean | null;
};

export type SearchFilters = Record<string, unknown>;

export type GeoChatSearchResponse = {
  assistant_message: string;
  extracted_filters: SearchFilters;
  anchor_place?: {
    place_id?: string;
    name: string;
    formatted_address?: string;
    lat: number;
    lng: number;
    area_name?: string;
  } | null;
  properties: GeoPropertySummary[];
  map_viewport?: { center: { lat: number; lng: number }; zoom: number; radius_km?: number } | null;
  suggested_replies: string[];
};

export type Landmark = {
  id: number;
  label: string;
  place_id?: string | null;
  lat: number;
  lng: number;
};

const SESSION_KEY = 'geo_anonymous_session_id';

export function getGeoSessionId(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

/** Align browser geo session with a DB-backed search thread (My searches restore). */
export function setGeoSessionId(sessionId: string): void {
  sessionStorage.setItem(SESSION_KEY, sessionId);
}

export async function ensureGeoSession(): Promise<string> {
  const existing = getGeoSessionId();
  if (existing) return existing;
  const res = await geoApi.post('/sessions/anonymous');
  const sessionId = res.data.session_id as string;
  setGeoSessionId(sessionId);
  return sessionId;
}

/** Drop cached geo session so a new chat does not inherit stale anchors/filters. */
export function clearGeoSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

/** Create and set a brand-new anonymous session ID (clears previous session). */
export async function createFreshGeoSession(): Promise<string> {
  clearGeoSession();
  const res = await geoApi.post('/sessions/anonymous');
  const sessionId = res.data.session_id as string;
  setGeoSessionId(sessionId);
  return sessionId;
}

export async function setGeoSessionScope(params: {
  session_id: string;
  country_code: string;
  city_id: number;
  city_name?: string;
  city_lat?: number;
  city_lng?: number;
}) {
  await geoApi.patch(`/sessions/${params.session_id}/scope`, {
    country_code: params.country_code,
    city_id: params.city_id,
    city_name: params.city_name,
    city_lat: params.city_lat,
    city_lng: params.city_lng,
  });
}

export async function fetchPopularLandmarks(cityId: number): Promise<Landmark[]> {
  const res = await geoApi.get('/locations/popular', { params: { city_id: cityId } });
  return res.data as Landmark[];
}

export async function sendGeoSearchMessage(body: {
  session_id: string;
  message: string;
  organization_id?: string;
  client_context: {
    country_code: string;
    city_id: number;
    city_name?: string;
    locale?: string;
    area_name?: string;
    anchor_lat?: number;
    anchor_lng?: number;
  };
}): Promise<GeoChatSearchResponse> {
  const res = await geoApi.post('/chat/search/geo/message', body);
  return res.data as GeoChatSearchResponse;
}

export async function sendGeoListingMessage(body: {
  session_id: string;
  message: string;
  draft_id?: string;
  organization_id?: string;
  client_context: {
    country_code: string;
    city_id: number;
    city_name?: string;
    locale?: string;
    anchor_lat?: number;
    anchor_lng?: number;
    area_name?: string;
  };
}) {
  const res = await geoApi.post('/chat/listing/geo/message', body);
  return res.data;
}

export async function confirmGeoListingLocation(body: {
  session_id: string;
  draft_id: string;
  lat: number;
  lng: number;
  formatted_address?: string;
  place_id?: string;
}) {
  const res = await geoApi.post('/chat/listing/geo/confirm-location', body);
  return res.data;
}


/** @deprecated Use resolveGoogleMapsApiKey from @/lib/googleMapsKey */
export async function getGoogleMapsBrowserKey(): Promise<string | null> {
  const { resolveGoogleMapsApiKey } = await import('@/lib/googleMapsKey');
  return resolveGoogleMapsApiKey();
}
